// FASE 05 — Cadastros Gerais, Catálogos (plano de 23/09/2026, Frentes D1/D2).
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import { buscarCadastro, enviarArquivo, listarTodos, salvarCadastro } from "services/modules/cadastrosGerais";

interface ICatalogo {
  idEsquadria3D: number;
  idReferencia: number;
  idLinha: number;
  idStatus: number;
  codigo: string;
  descricao?: string;
  linha?: string;
  linkArquivoGLB?: string;
  linkArquivoUSDZ?: string;
}

interface IOpcao {
  id: number;
  label: string;
}

interface ModalCatalogoProps {
  aberto: boolean;
  idEsquadria3D?: number;
  onFechar: (houveAlteracao: boolean) => void;
}

// Mesmo valor do enum StatusEsquadria3DEnum do backend.
const DISPONIVEL = 1;
const INDISPONIVEL = 0;

export function ModalCatalogo({ aberto, idEsquadria3D, onFechar }: ModalCatalogoProps) {
  const { openNotification } = useNavigation();
  const [id, setId] = useState<number | undefined>(idEsquadria3D);
  const [codigo, setCodigo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [referencia, setReferencia] = useState<IOpcao | null>(null);
  const [idLinha, setIdLinha] = useState<number | "">("");
  const [idStatus, setIdStatus] = useState<number>(INDISPONIVEL);
  const [modelos, setModelos] = useState<{ glb?: string; usdz?: string }>({});
  const [opcoesReferencias, setOpcoesReferencias] = useState<IOpcao[]>([]);
  const [opcoesLinhas, setOpcoesLinhas] = useState<IOpcao[]>([]);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState<"glb" | "usdz" | null>(null);
  const [houveAlteracao, setHouveAlteracao] = useState(false);

  // Linhas da referência escolhida (mesma fonte da tela legada: linhas vinculadas à referência).
  const carregarLinhas = async (idReferencia: number, linhaAtual?: IOpcao) => {
    const dados = await buscarCadastro<{ linhas: { idLinha: number; descricao: string }[] }>(
      "/Referencia",
      idReferencia
    );
    const opcoes = (dados?.linhas ?? []).map((l) => ({ id: l.idLinha, label: l.descricao }));
    // Catálogo antigo com linha que não está vinculada à referência: mantém a opção para não perdê-la.
    if (linhaAtual && !opcoes.some((o) => o.id === linhaAtual.id)) opcoes.push(linhaAtual);
    setOpcoesLinhas(opcoes);
  };

  const aplicar = async (catalogo: ICatalogo | null, referencias: IOpcao[]) => {
    setCodigo(catalogo?.codigo ?? "");
    setDescricao(catalogo?.descricao ?? "");
    setIdStatus(catalogo?.idStatus === DISPONIVEL ? DISPONIVEL : INDISPONIVEL);
    setModelos({ glb: catalogo?.linkArquivoGLB, usdz: catalogo?.linkArquivoUSDZ });
    const ref = catalogo ? referencias.find((r) => r.id === catalogo.idReferencia) ?? null : null;
    setReferencia(ref);
    setIdLinha(catalogo?.idLinha ?? "");
    if (catalogo?.idReferencia) {
      await carregarLinhas(catalogo.idReferencia, { id: catalogo.idLinha, label: catalogo.linha ?? "" });
    } else {
      setOpcoesLinhas([]);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    setId(idEsquadria3D);
    setErros({});
    setHouveAlteracao(false);

    const carregar = async () => {
      setCarregando(true);
      try {
        const referencias = (await listarTodos<any>("/Referencia")).map((r) => ({
          id: r.idReferencia,
          label: r.descricao,
        }));
        setOpcoesReferencias(referencias);
        const catalogo = idEsquadria3D ? await buscarCadastro<ICatalogo>("/Esquadria3D", idEsquadria3D) : null;
        await aplicar(catalogo, referencias);
      } catch (error: any) {
        openNotification({ message: error?.message || "Não foi possível carregar o catálogo", type: "error" });
      } finally {
        setCarregando(false);
      }
    };
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, idEsquadria3D]);

  const temOsDoisModelos = !!modelos.glb && !!modelos.usdz;

  const salvar = async () => {
    const novosErros: Record<string, string> = {};
    if (!codigo.trim()) novosErros.codigo = "Campo obrigatório";
    if (!referencia) novosErros.referencia = "Campo obrigatório";
    if (!idLinha) novosErros.idLinha = "Campo obrigatório";
    setErros(novosErros);
    if (Object.keys(novosErros).length) return;

    setSalvando(true);
    try {
      const resposta = await salvarCadastro<{ idEsquadria3D: number }>("/Esquadria3D", {
        idEsquadria3D: id ?? 0,
        codigo: codigo.trim(),
        descricao: descricao.trim(),
        idReferencia: referencia!.id,
        idLinha: Number(idLinha),
        idStatus,
      });
      setHouveAlteracao(true);
      const idGravado = resposta?.idEsquadria3D ?? id;
      if (!id && idGravado) {
        setId(idGravado);
        openNotification({ message: "Catálogo cadastrado. Agora envie os modelos 3D.", type: "success" });
        return;
      }
      openNotification({ message: "Catálogo salvo com sucesso!", type: "success" });
      onFechar(true);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível salvar o catálogo", type: "error" });
    } finally {
      setSalvando(false);
    }
  };

  const enviarModelo = async (formato: "glb" | "usdz", evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo || !id) return;
    if (!arquivo.name.toLowerCase().endsWith(`.${formato}`)) {
      openNotification({ message: `Selecione um arquivo .${formato}.`, type: "error" });
      return;
    }
    setEnviando(formato);
    try {
      const url = await enviarArquivo<string>(`/Esquadria3D/${id}/modelo/${formato}`, arquivo);
      setModelos((atual) => ({ ...atual, [formato]: url }));
      setHouveAlteracao(true);
      openNotification({ message: `Modelo ${formato.toUpperCase()} enviado com sucesso!`, type: "success" });
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível enviar o modelo", type: "error" });
    } finally {
      setEnviando(null);
    }
  };

  const botaoModelo = (formato: "glb" | "usdz") => {
    const url = modelos[formato];
    const campoId = `modelo-${formato}-input`;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <input
          accept={`.${formato}`}
          style={{ display: "none" }}
          id={campoId}
          type="file"
          disabled={!id}
          onChange={(evento) => enviarModelo(formato, evento)}
        />
        <label htmlFor={campoId}>
          <LoadingButton
            component="span"
            variant="outlined"
            startIcon={<CloudUpload />}
            disabled={!id}
            loading={enviando === formato}
          >
            Enviar {formato.toUpperCase()}
          </LoadingButton>
        </label>
        {url ? (
          <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
            Modelo {formato.toUpperCase()} enviado
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sem modelo {formato.toUpperCase()}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={aberto} onClose={() => onFechar(houveAlteracao)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Cadastro de catálogo
        <IconButton onClick={() => onFechar(houveAlteracao)} sx={{ position: "absolute", right: 8, top: 8 }} aria-label="Fechar">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Código"
              value={codigo}
              onChange={(evento) => setCodigo(evento.target.value)}
              error={!!erros.codigo}
              helperText={erros.codigo ?? "Obrigatório"}
              inputProps={{ maxLength: 40 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Descrição" value={descricao} onChange={(evento) => setDescricao(evento.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={opcoesReferencias}
              value={referencia}
              loading={carregando}
              onChange={async (_, opcao) => {
                setReferencia(opcao);
                setIdLinha("");
                setOpcoesLinhas([]);
                if (opcao) await carregarLinhas(opcao.id);
              }}
              getOptionLabel={(opcao) => opcao.label}
              isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
              renderInput={(params) => (
                <TextField {...params} label="Referência" error={!!erros.referencia} helperText={erros.referencia ?? "Obrigatório"} />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Linha"
              value={idLinha}
              onChange={(evento) => setIdLinha(Number(evento.target.value))}
              error={!!erros.idLinha}
              helperText={erros.idLinha ?? (referencia && opcoesLinhas.length === 0 ? "A referência não tem linhas vinculadas" : "Obrigatório")}
            >
              {opcoesLinhas.map((opcao) => (
                <MenuItem key={opcao.id} value={opcao.id}>
                  {opcao.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Status"
              value={idStatus}
              onChange={(evento) => setIdStatus(Number(evento.target.value))}
              helperText={temOsDoisModelos ? undefined : "Disponível só depois de enviar os dois modelos 3D"}
            >
              <MenuItem value={DISPONIVEL} disabled={!temOsDoisModelos}>
                Disponível
              </MenuItem>
              <MenuItem value={INDISPONIVEL}>Indisponível</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Modelos 3D
            </Typography>
            {!id && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Salve o catálogo para enviar os modelos.
              </Typography>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {botaoModelo("glb")}
              {botaoModelo("usdz")}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <Grid container justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
        <Grid item>
          <LoadingButton variant="outlined" onClick={() => onFechar(houveAlteracao)} disabled={salvando}>
            {id ? "Fechar" : "Cancelar"}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton variant="contained" onClick={salvar} loading={salvando} disabled={carregando}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
}
